import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";

const Navigation = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const [dropdownTimer, setDropdownTimer] = useState(null);

  const handleMouseEnter = (menu) => {
    // Clear any existing timer
    if (dropdownTimer) {
      clearTimeout(dropdownTimer);
      setDropdownTimer(null);
    }
    setActiveDropdown(menu);
  };

  const handleMouseLeave = () => {
    // Add a small delay before closing
    const timer = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
    setDropdownTimer(timer);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setMobileDropdownOpen(null);
  };

  const toggleMobileDropdown = (menu) => {
    setMobileDropdownOpen(mobileDropdownOpen === menu ? null : menu);
  };

  return (
    <nav className="bg-white border-b border-luxury-gold/20 sticky top-0 z-50 backdrop-blur-sm bg-white/95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24 gap-2">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_timber-decks/artifacts/0s5xkwbn_TBS%20Logo%20-%20timberlake%20flooring.png" 
              alt="Timberlake Flooring Logo" 
              className="h-10 sm:h-12 w-auto"
            />
            <div className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold tracking-wider luxury-heading">
              <div className="flex flex-col items-start leading-tight">
                <div>
                  <span className="text-luxury-navy">TIMBERLAKE</span>
                  <span className="text-luxury-gold ml-1">FLOORING</span>
                </div>
                <span className="text-luxury-gold text-sm sm:text-base md:text-lg italic font-light tracking-wide">Factory Direct</span>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex items-center space-x-6 lg:space-x-8 flex-nowrap">
            <Link
              to="/"
              className="text-luxury-slate hover:text-luxury-gold font-medium transition-all duration-300 relative group whitespace-nowrap"
            >
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-luxury-gold group-hover:w-full transition-all duration-300"></span>
            </Link>

            <Link
              to="/request-quote"
              className="text-luxury-slate hover:text-luxury-gold font-medium transition-all duration-300 relative group whitespace-nowrap"
            >
              Quote
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-luxury-gold group-hover:w-full transition-all duration-300"></span>
            </Link>

            {/* Products Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter("products")}
              onMouseLeave={handleMouseLeave}
            >
              <div className="flex items-center cursor-pointer">
                <Link
                  to="/products"
                  className="text-luxury-slate hover:text-luxury-gold font-medium transition-all duration-300 relative group"
                >
                  Products
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-luxury-gold group-hover:w-full transition-all duration-300"></span>
                </Link>
                <button 
                  className="ml-1 text-luxury-slate hover:text-luxury-gold transition-colors duration-300"
                  onClick={() => setActiveDropdown(activeDropdown === "products" ? null : "products")}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              {activeDropdown === "products" && (
                <div 
                  className="absolute left-0 mt-2 w-96 bg-white border border-luxury-gold/30 shadow-2xl rounded-lg p-6 z-[100]"
                  onMouseEnter={() => handleMouseEnter("products")}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-luxury-navy mb-3 text-sm uppercase tracking-wider">
                        Flooring Types
                      </h3>
                      <ul className="space-y-3">
                        <li>
                          <Link
                            to="/products?filter=laminate"
                            onClick={() => setActiveDropdown(null)}
                            className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                          >
                            Water-Resistant Laminate
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/products?filter=vinyl"
                            onClick={() => setActiveDropdown(null)}
                            className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                          >
                            Rigid-Core Vinyl (SPC)
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/products?filter=hardwood"
                            onClick={() => setActiveDropdown(null)}
                            className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                          >
                            Engineered Hardwood
                          </Link>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold text-luxury-navy mb-3 text-sm uppercase tracking-wider">
                        Accessories
                      </h3>
                      <ul className="space-y-3">
                        <li>
                          <Link
                            to="/products?filter=underlay"
                            onClick={() => setActiveDropdown(null)}
                            className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                          >
                            Underlayments
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/products?filter=stair_nosings"
                            onClick={() => setActiveDropdown(null)}
                            className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                          >
                            Stair Nosings
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/products?filter=t-mouldings"
                            onClick={() => setActiveDropdown(null)}
                            className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                          >
                            T-Mouldings
                          </Link>
                        </li>
                        <li>
                          <Link
                            to="/products?filter=reducers"
                            onClick={() => setActiveDropdown(null)}
                            className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                          >
                            Reducers
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                  {/* Room Visualizer Link */}
                  <div className="mt-4 pt-4 border-t border-luxury-gold/20">
                    <Link
                      to="/visualizer"
                      onClick={() => setActiveDropdown(null)}
                      className="flex items-center gap-2 text-luxury-navy hover:text-luxury-gold transition-colors duration-300 font-medium"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Room Visualizer
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className="text-luxury-slate hover:text-luxury-gold font-medium transition-all duration-300 relative group whitespace-nowrap"
            >
              About
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-luxury-gold group-hover:w-full transition-all duration-300"></span>
            </Link>

            {/* Resources Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => handleMouseEnter("resources")}
              onMouseLeave={handleMouseLeave}
            >
              <div className="flex items-center cursor-pointer">
                <Link
                  to="/resources"
                  className="text-luxury-slate hover:text-luxury-gold font-medium transition-all duration-300 relative group"
                >
                  Resources
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-luxury-gold group-hover:w-full transition-all duration-300"></span>
                </Link>
                <button 
                  className="ml-1 text-luxury-slate hover:text-luxury-gold transition-colors duration-300"
                  onClick={() => setActiveDropdown(activeDropdown === "resources" ? null : "resources")}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              {activeDropdown === "resources" && (
                <div 
                  className="absolute left-0 mt-2 w-80 bg-white border border-luxury-gold/30 shadow-2xl rounded-lg p-6 z-[100]"
                  onMouseEnter={() => handleMouseEnter("resources")}
                  onMouseLeave={handleMouseLeave}
                >
                  <div>
                    <h3 className="font-semibold text-luxury-navy mb-3 text-sm uppercase tracking-wider">
                      Product Documentation
                    </h3>
                    <ul className="space-y-3 mb-4">
                      <li>
                        <Link
                          to="/resources#sell-sheets"
                          onClick={() => setActiveDropdown(null)}
                          className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                        >
                          Sell Sheets
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/resources#catalogues"
                          onClick={() => setActiveDropdown(null)}
                          className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                        >
                          Product Catalogues
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/resources#warranties"
                          onClick={() => setActiveDropdown(null)}
                          className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                        >
                          Product Warranties
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/resources#certifications"
                          onClick={() => setActiveDropdown(null)}
                          className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                        >
                          Certifications
                        </Link>
                      </li>
                    </ul>
                    <h3 className="font-semibold text-luxury-navy mb-3 text-sm uppercase tracking-wider">
                      Installation & Maintenance
                    </h3>
                    <ul className="space-y-3">
                      <li>
                        <Link
                          to="/resources#installation"
                          onClick={() => setActiveDropdown(null)}
                          className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                        >
                          Installation Instructions
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/resources#maintenance"
                          onClick={() => setActiveDropdown(null)}
                          className="text-luxury-slate hover:text-luxury-gold transition-colors duration-300 block py-1"
                        >
                          Care & Maintenance Guides
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className="text-luxury-slate hover:text-luxury-gold font-medium transition-all duration-300 relative group whitespace-nowrap"
            >
              Contact
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-luxury-gold group-hover:w-full transition-all duration-300"></span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors relative z-50 flex-shrink-0"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-7 h-7 text-black stroke-[3]" />
            ) : (
              <Menu className="w-7 h-7 text-black stroke-[3]" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col space-y-4">
              <Link
                to="/"
                className="text-gray-700 hover:text-blue-600 font-medium px-4 py-2"
                onClick={toggleMobileMenu}
              >
                Home
              </Link>

              <Link
                to="/request-quote"
                className="text-gray-700 hover:text-blue-600 font-medium px-4 py-2"
                onClick={toggleMobileMenu}
              >
                Request Quote
              </Link>

              {/* Mobile Products Dropdown */}
              <div>
                <button
                  className="flex items-center justify-between w-full text-gray-700 hover:text-blue-600 font-medium px-4 py-2"
                  onClick={() => toggleMobileDropdown("products")}
                >
                  Products
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      mobileDropdownOpen === "products" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {mobileDropdownOpen === "products" && (
                  <div className="bg-gray-50 py-2 px-4 space-y-2">
                    <div className="mb-3">
                      <p className="font-semibold text-gray-800 text-sm mb-2">
                        Flooring Types
                      </p>
                      <Link
                        to="/products?filter=laminate"
                        className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                        onClick={toggleMobileMenu}
                      >
                        Water-Resistant Laminate
                      </Link>
                      <Link
                        to="/products?filter=hardwood"
                        className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                        onClick={toggleMobileMenu}
                      >
                        Engineered Hardwood
                      </Link>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm mb-2">
                        Collections
                      </p>
                      <Link
                        to="/products?filter=ComfortSense"
                        className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                        onClick={toggleMobileMenu}
                      >
                        ComfortSense
                      </Link>
                      <Link
                        to="/products?filter=SolidSense"
                        className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                        onClick={toggleMobileMenu}
                      >
                        SolidSense
                      </Link>
                      <Link
                        to="/products?filter=NatureSense"
                        className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                        onClick={toggleMobileMenu}
                      >
                        NatureSense
                      </Link>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm mb-2">
                        Accessories
                      </p>
                      <Link
                        to="/products?filter=underlay"
                        className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                        onClick={toggleMobileMenu}
                      >
                        Underlayments
                      </Link>
                      <Link
                        to="/products?filter=stair_nosings"
                        className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                        onClick={toggleMobileMenu}
                      >
                        Stair Nosings
                      </Link>
                      <Link
                        to="/products?filter=t-mouldings"
                        className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                        onClick={toggleMobileMenu}
                      >
                        T-Mouldings
                      </Link>
                      <Link
                        to="/products?filter=reducers"
                        className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                        onClick={toggleMobileMenu}
                      >
                        Reducers
                      </Link>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <Link
                        to="/visualizer"
                        className="flex items-center gap-2 text-luxury-navy hover:text-luxury-gold py-1 text-sm font-medium"
                        onClick={toggleMobileMenu}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Room Visualizer
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/about"
                className="text-gray-700 hover:text-blue-600 font-medium px-4 py-2"
                onClick={toggleMobileMenu}
              >
                About Us
              </Link>

              {/* Mobile Resources Dropdown */}
              <div>
                <button
                  className="flex items-center justify-between w-full text-gray-700 hover:text-blue-600 font-medium px-4 py-2"
                  onClick={() => toggleMobileDropdown("resources")}
                >
                  Resources
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      mobileDropdownOpen === "resources" ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {mobileDropdownOpen === "resources" && (
                  <div className="bg-gray-50 py-2 px-4 space-y-2">
                    <Link
                      to="/resources#sell-sheets"
                      className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                      onClick={toggleMobileMenu}
                    >
                      Sell Sheets
                    </Link>
                    <Link
                      to="/resources#catalogues"
                      className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                      onClick={toggleMobileMenu}
                    >
                      Product Catalogues
                    </Link>
                    <Link
                      to="/resources#warranties"
                      className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                      onClick={toggleMobileMenu}
                    >
                      Product Warranties
                    </Link>
                    <Link
                      to="/resources#installation"
                      className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                      onClick={toggleMobileMenu}
                    >
                      Installation Instructions
                    </Link>
                    <Link
                      to="/resources#maintenance"
                      className="block text-gray-600 hover:text-blue-600 py-1 text-sm"
                      onClick={toggleMobileMenu}
                    >
                      Care & Maintenance
                    </Link>
                  </div>
                )}
              </div>

              <Link
                to="/contact"
                className="text-gray-700 hover:text-blue-600 font-medium px-4 py-2"
                onClick={toggleMobileMenu}
              >
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
